import { action } from '@/lib/api';
import clone from '@/lib/clone';
import pushChangesQueueItem from '@/redux/actions/pushChangesQueueItem';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import { store } from '@/redux/lib/configureStore';
import cookUpdatedWorkflowStep from '@/screens/workflows/lib/cookUpdatedWorkflowStep';
import { getDeep } from '@shared/utils/getDeep.js';
import { setDeep } from '@shared/utils/setDeep.js';
import { validateScheduleArguments } from '@shared/workflow/scheduleArgumentsSchema.js';
import fetchWorkflowStepLongPollResponses from '../../workflowEditor/workflowEditorPanels/primaryPanels/webhookPanel/triggerStatusPane/utils/fetchWorkflowStepLongPollResponses';
import workflowScreenSelectors from '../selectors/workflowScreenSelectors';

/**
 * Cooking a step is asynchronous, so two `set` calls for the same step overlap: each one
 * snapshots the store, dispatches its optimistic step, then awaits its own cook before
 * dispatching and queueing the cooked result. Unordered, whichever cook resolves LAST wins
 * and writes its older value over the newer one — both into redux and into the changes queue
 * (`PUSH_CHANGES_QUEUE_ITEM` collapses by uniqueKey, so the last push for a path is what the
 * server receives). That is how a field the user is typing into silently reverts itself.
 *
 * Cooked writes for a given step are therefore serialized. The optimistic dispatch stays
 * synchronous so the editor still reacts immediately; only the cook tail queues.
 *
 * A queued write must always settle, or the queue for that step stalls and every later write to
 * it is stranded. `set` therefore keeps its existing contract of never rejecting: the task logs
 * and returns instead, so the 149 callers that do not await it gain no rejection to handle.
 */
const stepCookQueues = new Map();

const enqueueStepCook = (stepId, task) => {
  const previous = stepCookQueues.get(stepId) || Promise.resolve();
  const current = previous.then(task);

  stepCookQueues.set(stepId, current);
  current.then(() => {
    if (stepCookQueues.get(stepId) === current) {
      stepCookQueues.delete(stepId);
    }
  });

  return current;
};

const stepActions = {
  set(path, payload, opts = {}) {
    const state = store.getState();
    const stepId = opts?.stepId;
    const stepSelector = stepId ? workflowScreenSelectors.activeWorkflowStepById(state, stepId) : workflowScreenSelectors.activeWorkflowStep(state);
    const activeStep = clone(stepSelector);
    const newStep = setDeep(activeStep, path, payload);

    action('PUT_WORKFLOW_STEP', newStep);

    return enqueueStepCook(newStep?.id, async () => {
      try {
        const cookedStep = await cookUpdatedWorkflowStep(state, newStep);
        if (!cookedStep.success) {
          console.error('Failed cooking step', cookedStep);
          return;
        }

        if (payload === null) {
          cookedStep.data = setDeep(cookedStep?.data, path, null);
        }

        const value = getDeep(path, cookedStep?.data);
        action('PUT_WORKFLOW_STEP', cookedStep?.data);
        pushChangesQueueItem('workflowStep', { workflowId: activeStep?.workflowId, stepId: newStep?.id, path, value });

        if (activeStep?.options?.triggerPoll && path.startsWith('arguments')) {
          action('SET_LONG_POLL_RESPONSE', { stepId: newStep?.id, data: {} });
          waitForSyncReady().then(() => fetchWorkflowStepLongPollResponses({ workflowId: activeStep?.workflowId, stepId: newStep?.id }));
        }
      } catch (error) {
        console.error('Failed setting step path', path, error);
      }
    });
  },

  setSchedule(args, opts = {}) {
    return new Promise((resolve) => {
      const state = store.getState();
      const workflow = workflowScreenSelectors.activeWorkflow(state);
      const stepId = opts?.stepId;
      const step = stepId ? workflowScreenSelectors.activeWorkflowStepById(state, stepId) : workflowScreenSelectors.activeWorkflowStep(state);

      // An incomplete schedule (Fixed Times with no run time yet) is never selected by the
      // scheduler, so it must not reach main.workflow_schedule: the step arguments still save
      // and the last complete schedule stays in effect until this one is complete.
      if (!validateScheduleArguments(args).ok) {
        resolve();
        return;
      }

      pushChangesQueueItem('workflowSchedule', { workflowId: workflow?.id, stepId: step?.id, value: args });
      resolve();
    });
  },

  setPartialSchedule(path, payload, stepId) {
    return new Promise((resolve) => {
      const state = store.getState();
      const workflow = workflowScreenSelectors.activeWorkflow(state);
      const step = stepId ? workflowScreenSelectors.activeWorkflowStepById(state, stepId) : workflowScreenSelectors.activeWorkflowStep(state);

      const newStep = setDeep(step, path, payload);
      const value = newStep?.arguments;

      if (!validateScheduleArguments(value).ok) {
        resolve();
        return;
      }

      pushChangesQueueItem('workflowSchedule', { workflowId: workflow?.id, stepId: step?.id, value });
      resolve();
    });
  }
};

export default stepActions;
